// Updated Engineer Header - frontend/src/components/engineer/EngineerHeader.tsx
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { AuthUser } from '../../services/auth.service';
import styles from './EngineerHeader.module.css';

interface EngineerHeaderProps {
  user: AuthUser;
  onLogout: () => void;
  onNavigate?: (page: string, params?: any) => void;
  onChangePassword?: () => void; // ADD THIS PROP
}

const EngineerHeader: React.FC<EngineerHeaderProps> = ({ 
  user, 
  onLogout, 
  onNavigate,
  onChangePassword // ADD THIS
}) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Handle clicks outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleUserMenuToggle = useCallback(() => {
    setShowUserMenu(prev => !prev);
  }, []);

  // ADD THIS HANDLER - Change Password
  const handleChangePassword = useCallback(() => {
    console.log('👤 Engineer Header: Change Password clicked');
    setShowUserMenu(false);
    if (onChangePassword) {
      onChangePassword();
    }
  }, [onChangePassword]);

  const handleLogout = useCallback(() => {
    console.log('👤 Engineer Header: Logout action');
    onLogout();
    setShowUserMenu(false);
  }, [onLogout]);

  return (
    <header className={styles.engineerHeader}>
      {/* Logo and Brand */}
      <div className={styles.headerLeft}>
        <div className={styles.logo}>
          <div className={styles.logoIcon}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
              <path 
                d="M12 2L2 7L12 12L22 7L12 2Z" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinejoin="round"
              />
              <path 
                d="M2 17L12 22L22 17" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinejoin="round"
              />
              <path 
                d="M2 12L12 17L22 12" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className={styles.logoText}>
            <span className={styles.brandName}>PHOENICIAN</span>
            <span className={styles.brandSubtitle}>Engineering Portal</span>
          </div>
        </div>
      </div>

      {/* Center - Simple Title */}
      <div className={styles.headerCenter}>
        <h2 style={{ color: 'white', margin: 0, fontSize: '1.25rem' }}>
          PHOENICIAN TECHNICAL SERVICES LLC
        </h2>
      </div>

      {/* Right side - User Menu Only */}
      <div className={styles.headerRight}>
        {/* User Menu */}
        <div className={styles.userMenuContainer} ref={userMenuRef}>
          <button 
            className={styles.userMenuBtn}
            onClick={handleUserMenuToggle}
            aria-label="User menu"
          >
            <div className={styles.userAvatar}>
              {user.name ? user.name.charAt(0).toUpperCase() : 'E'}
            </div>
            <div className={styles.userInfo}>
              <span className={styles.userName}>{user.name || 'Engineer'}</span>
              <span className={styles.userRole}>{user.designation || 'Engineer'}</span>
            </div>
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none"
              className={`${styles.chevronIcon} ${showUserMenu ? styles.rotated : ''}`}
            >
              <polyline points="6,9 12,15 18,9" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </button>

          {showUserMenu && (
            <div className={styles.userDropdown}>
              <div className={styles.userDropdownHeader}>
                <div className={styles.dropdownAvatar}>
                  {user.name ? user.name.charAt(0).toUpperCase() : 'E'}
                </div>
                <div className={styles.dropdownUserInfo}>
                  <div className={styles.dropdownUserName}>{user.name || 'Engineer'}</div>
                  <div className={styles.dropdownUserEmail}>{user.email}</div>
                  <div className={styles.dropdownUserDepartment}>{user.department || 'Engineering'}</div>
                </div>
              </div>

              <div className={styles.userDropdownActions}>
                {/* ADD THIS - Change Password Option */}
                <button 
                  className={styles.dropdownAction}
                  onClick={handleChangePassword}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                    <circle cx="12" cy="16" r="1" fill="currentColor"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  Change Password
                </button>

                {/* ADD THIS - Divider */}
                <div className={styles.dropdownDivider}></div>

                <button 
                  className={`${styles.dropdownAction} ${styles.logoutAction}`}
                  onClick={handleLogout}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2"/>
                    <polyline points="16,17 21,12 16,7" stroke="currentColor" strokeWidth="2"/>
                    <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  Sign Out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default EngineerHeader;